import { useCallback, useEffect, useRef, useState } from "react";
import MenuItem from "./MenuItem";
import { ReactSVG } from "react-svg";
import logo from "../../assets/logo.svg";
import homeOutline from "../../assets/svg/home-stroke.svg";
import homeFilled from "../../assets/svg/material-symbols--home-rounded.svg";
import projectsOutline from "../../assets/svg/grid-stroke.svg";
import projectsFilled from "../../assets/svg/mingcute--grid-fill.svg";
import translate from "../../assets/svg/translate.svg";
import arrow from "../../assets/svg/mdi--arrow-up.svg";
import backIcon from "../../assets/svg/mdi--arrow-left.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n/LanguageContext";

const LANG_PANEL_WIDTH = 60;
const LANG_PANEL_GAP = 12;
const LANG_PANEL_GUTTER = 15;
const HEADER_SCROLL_DISTANCE = 140;

function useIsMobile(bp = 768) {
   const [isMobile, setIsMobile] = useState(() =>
      typeof window !== "undefined"
         ? window.matchMedia(`(max-width:${bp}px)`).matches
         : false,
   );

   useEffect(() => {
      const mq = window.matchMedia(`(max-width:${bp}px)`);
      const onChange = (e) => setIsMobile(e.matches);

      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else mq.addListener(onChange);

      setIsMobile(mq.matches);

      return () => {
         if (mq.removeEventListener) mq.removeEventListener("change", onChange);
         else mq.removeListener(onChange);
      };
   }, [bp]);

   return isMobile;
}

export default function Header() {
   const { language, languages, setLanguage, t } = useTranslation();
   const location = useLocation();
   const navigate = useNavigate();
   const isMobile = useIsMobile(768);
   const logoRef = useRef(null);
   const langWrapRef = useRef(null);
   const langPanelRef = useRef(null);
   const langOpenRef = useRef(false);
   const showScrollTopRef = useRef(false);
   const isScrolledRef = useRef(false);
   const syncHeaderToScrollRef = useRef(() => {});
   const shouldRunLogoIntro = location.pathname === "/";

   const [langOpen, setLangOpen] = useState(false);
   const [langPanelPosition, setLangPanelPosition] = useState({
      left: 0,
      top: 0,
   });
   const [showScrollTop, setShowScrollTop] = useState(false);
   const [isScrolled, setIsScrolled] = useState(false);
   const [logoIntro, setLogoIntro] = useState({
      visible: shouldRunLogoIntro,
      flying: false,
      x: 0,
      y: 0,
      scale: 0.62,
   });
   const showBack = location.pathname !== "/";

   useEffect(() => {
      langOpenRef.current = langOpen;
   }, [langOpen]);

   useEffect(() => {
      if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
         window.history.scrollRestoration = "auto";
      }
   }, []);

   useEffect(() => {
      if (typeof window === "undefined") return;
      if (!shouldRunLogoIntro) {
         setLogoIntro((value) =>
            value.visible ? { ...value, visible: false } : value,
         );
         return;
      }

      const flyTimer = setTimeout(() => {
         const rect = logoRef.current?.getBoundingClientRect();
         if (!rect) {
            setLogoIntro((value) => ({ ...value, visible: false }));
            return;
         }

         const targetCenterX = rect.left + rect.width / 2;
         const targetCenterY = rect.top + rect.height / 2;
         const y = window.scrollY || document.documentElement.scrollTop || 0;
         const isPastThreshold = y > HEADER_SCROLL_DISTANCE;

         setLogoIntro({
            visible: true,
            flying: true,
            x: targetCenterX - window.innerWidth / 2,
            y: targetCenterY - window.innerHeight / 2,
            scale: isPastThreshold ? 0.88 : 1,
         });
      }, 850);

      const hideTimer = setTimeout(() => {
         setLogoIntro((value) => ({ ...value, visible: false }));
      }, 2000);

      return () => {
         clearTimeout(flyTimer);
         clearTimeout(hideTimer);
      };
   }, [shouldRunLogoIntro]);

   const updateLangPanelPosition = useCallback(() => {
      const trigger = langWrapRef.current;
      if (!trigger || typeof window === "undefined") return;

      const rect = trigger.getBoundingClientRect();
      const maxLeft = window.innerWidth - LANG_PANEL_WIDTH - LANG_PANEL_GUTTER;
      const centeredLeft = rect.left + rect.width / 2 - LANG_PANEL_WIDTH / 2;

      setLangPanelPosition({
         left: Math.max(LANG_PANEL_GUTTER, Math.min(centeredLeft, maxLeft)),
         top: rect.bottom + LANG_PANEL_GAP,
      });
   }, []);

   useEffect(() => {
      if (!langOpen) return;

      const onDown = (e) => {
         const wrapper = langWrapRef.current;
         const panel = langPanelRef.current;
         if (wrapper?.contains(e.target) || panel?.contains(e.target)) return;
         setLangOpen(false);
      };

      const onKey = (e) => {
         if (e.key === "Escape") setLangOpen(false);
      };

      window.addEventListener("mousedown", onDown, true);
      window.addEventListener("touchstart", onDown, true);
      window.addEventListener("keydown", onKey);

      return () => {
         window.removeEventListener("mousedown", onDown, true);
         window.removeEventListener("touchstart", onDown, true);
         window.removeEventListener("keydown", onKey);
      };
   }, [langOpen]);

   useEffect(() => {
      if (!langOpen) return;

      updateLangPanelPosition();

      window.addEventListener("resize", updateLangPanelPosition);
      window.addEventListener("scroll", updateLangPanelPosition, {
         passive: true,
      });

      return () => {
         window.removeEventListener("resize", updateLangPanelPosition);
         window.removeEventListener("scroll", updateLangPanelPosition);
      };
   }, [langOpen, updateLangPanelPosition]);

   useEffect(() => {
      const applyScrollState = (y) => {
         const shouldShowScrollTop = y > HEADER_SCROLL_DISTANCE;
         if (showScrollTopRef.current !== shouldShowScrollTop) {
            showScrollTopRef.current = shouldShowScrollTop;
            setShowScrollTop(shouldShowScrollTop);
         }

         if (isScrolledRef.current !== shouldShowScrollTop) {
            isScrolledRef.current = shouldShowScrollTop;
            setIsScrolled(shouldShowScrollTop);
         }

         if (langOpenRef.current) updateLangPanelPosition();
      };

      syncHeaderToScrollRef.current = () => {
         const y = window.scrollY || document.documentElement.scrollTop || 0;
         applyScrollState(y);
      };

      const onScroll = () => {
         const y = window.scrollY || document.documentElement.scrollTop || 0;
         applyScrollState(y);
      };

      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });

      return () => {
         window.removeEventListener("scroll", onScroll);
         syncHeaderToScrollRef.current = () => {};
      };
   }, [updateLangPanelPosition]);

   const onLangPick = (code) => {
      setLanguage(code);
      setLangOpen(false);
   };

   const onLangToggle = () => {
      if (!langOpen) updateLangPanelPosition();
      setLangOpen((value) => !value);
   };

   const onScrollTop = () => {
      try {
         window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
         window.scrollTo(0, 0);
      }
   };

   const onBack = () => {
      if (location.pathname.startsWith("/projects/")) {
         navigate("/projects");
         return;
      }

      if (location.pathname === "/projects" || location.pathname.startsWith("/services/")) {
         navigate("/", { state: { fastHomeReturn: true } });
         return;
      }

      navigate("/");
   };

   const mobileActionIsBack = showBack && !showScrollTop;
   const mobileActionVisible = showScrollTop || showBack;

   const onMobileAction = () => {
      if (mobileActionIsBack) {
         onBack();
         return;
      }

      onScrollTop();
   };

   return (
      <>
         <header className={`header ${logoIntro.visible && shouldRunLogoIntro ? "is-logo-intro" : ""} ${isScrolled ? "is-scrolled" : ""}`}>
            <div className="container">
               <div className="header__body">
                  <div className="header__brand">
                     <button
                        type="button"
                        className={`header__back glass-effect glass-union-effect ${showBack ? "is-visible" : ""}`}
                        onClick={onBack}
                        aria-label={t("common.back")}
                        aria-hidden={!showBack}
                        tabIndex={showBack ? 0 : -1}
                        title={t("common.back")}
                     >
                        <ReactSVG
                           className="header__back-icon"
                           src={backIcon}
                        />
                     </button>

                     <Link
                        ref={logoRef}
                        to="/"
                        className="header__logo glass-effect"
                     >
                        <ReactSVG className="header__svg" src={logo} />
                     </Link>
                  </div>

                  <div className="header__actions">
                     {!isMobile && (
                        <div
                           className="header__left-wrapper glass-effect glass-union-effect"
                        >
                           <div className="header__left">
                              <nav className="menu menu--desktop">
                                 <ul className="menu__list">
                                    <MenuItem
                                       path="/"
                                       end
                                       icon={homeOutline}
                                       activeIcon={homeFilled}
                                       label={t("nav.home")}
                                    />

                                    <MenuItem
                                       path="/projects"
                                       icon={projectsOutline}
                                       activeIcon={projectsFilled}
                                       label={t("nav.projects")}
                                    />

                                    {/* Asistent is hidden until that section is ready. */}
                                 </ul>
                              </nav>
                           </div>
                        </div>
                     )}

                     <div
                        ref={langWrapRef}
                        className={`lang settings settings--lang glass-effect glass-union-effect ${langOpen ? "is-open" : ""}`}
                     >
                        <button
                           type="button"
                           className="settings__mode-btn lang__btn"
                           onClick={onLangToggle}
                           aria-haspopup="menu"
                           aria-expanded={langOpen}
                           aria-label={t("nav.language")}
                           title={t("nav.language")}
                        >
                           <span className="lang__btn-inner">
                              <ReactSVG
                                 className="menu__icon"
                                 src={translate}
                              />
                           </span>
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         </header>

         {logoIntro.visible && shouldRunLogoIntro && (
            <>
               <div
                  className={`logo-loader-bg ${logoIntro.flying ? "is-flying" : ""}`}
                  aria-hidden="true"
               />
               <div
                  className={`logo-loader-pill header__logo glass-effect ${logoIntro.flying ? "is-flying" : ""}`}
                  aria-hidden="true"
                  style={{
                     "--logo-loader-x": `${logoIntro.x}px`,
                     "--logo-loader-y": `${logoIntro.y}px`,
                     "--logo-loader-scale": logoIntro.scale,
                  }}
               >
                  <ReactSVG
                     className="logo-loader-pill__svg header__svg"
                     src={logo}
                  />
               </div>
            </>
         )}

         <div
            ref={langPanelRef}
            className={`lang__panel glass-effect glass-union-effect ${langOpen ? "is-open" : ""}`}
            role="menu"
            aria-hidden={!langOpen}
            style={{
               left: `${langPanelPosition.left}px`,
               top: `${langPanelPosition.top}px`,
            }}
         >
            {languages.map((code) => (
               <button
                  key={code}
                  type="button"
                  role="menuitem"
                  className={`lang__item ${code === language ? "is-active" : ""}`}
                  onClick={() => onLangPick(code)}
               >
                  {code}
               </button>
            ))}
         </div>

         {isMobile && (
            <nav className={`mobile-bar ${isScrolled ? "is-scrolled" : ""}`} aria-label={t("common.bottomNavigation")}>
               <div className="mobile-bar__row">
                  <div className="mobile-bar__inner glass-effect glass-union-effect">
                     <div className="mobile-bar__menu">
                        <ul className="menu__list">
                           <MenuItem
                              path="/"
                              end
                              icon={homeOutline}
                              activeIcon={homeFilled}
                              label={t("nav.home")}
                           />

                           <MenuItem
                              path="/projects"
                              icon={projectsOutline}
                              activeIcon={projectsFilled}
                              label={t("nav.projects")}
                           />
                        </ul>
                     </div>
                  </div>

                  <button
                     type="button"
                     className={`mobile-scroll-top glass-effect glass-union-effect ${mobileActionVisible ? "is-visible" : ""} ${mobileActionIsBack ? "is-back-mode" : ""}`}
                     onClick={onMobileAction}
                     aria-label={mobileActionIsBack ? t("common.back") : t("nav.top")}
                     aria-hidden={!mobileActionVisible}
                     tabIndex={mobileActionVisible ? 0 : -1}
                     title={mobileActionIsBack ? t("common.back") : t("nav.top")}
                  >
                     <ReactSVG
                        className="mobile-scroll-top__icon"
                        src={arrow}
                     />
                  </button>
               </div>
            </nav>
         )}
      </>
   );
}
